import BotStatusComponent from '@/common/components/bot-status/bot-status';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import BTButton from '@common-components/bt-button/bt-button';
import BTThemeProvider from '@common-components/bt-theme-provider';
import RowStack from '@common-components/common/row-stack/RowStack';
import ExtensionTopButtonBar from '@common-components/top-menu/extension-top-button-bar';
import { namespace } from "@common-config/constants";
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import { activeSubscriptionSelector, isLabelsEnabledSelector, selectUser } from '@common-reducers/UserSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { labelActions } from '@common-reducers/labels-reducer';
import { loadLabelsAndContactLabelsThunk } from '@common-reducers/labels-reducer/labels-thunks/crud';
import ConditionalWrapper from '@common/components/conditional-wrapper/ConditionalWrapper';
import ConnectedEngines from '@common/components/connected-engines/connected-engines';
import SubscriptionStatusBar from '@common/components/subscription-status-bar';
import UpdateNotificationBadge from '@common/components/update-notification/update-notification-badge';
import { isAnyCampaignInProgressSelector } from '@common/reducers/CampaignSelectors';
import { getWaVersionThunk } from "@extension-context-content/thunks/WhatsappThunks";
import { waQueryContactsAndChatsTool } from "@common/tools/whatsapp";
import { Plus } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import InProgressCampaignsIndicatorDialog from '../bt-bulk-dashboard/in-progress-campaigns-indicator-dialog';
import LabelTopHeader from './label-top-header/label-top-header';
import LabelEmptyState from './label-empty-state';
import LabelSettingsPopper from './poppers/label-settings-popper';
import LanguageSwitcher from '@/common/components/language-switcher/language-switcher';
import ShadowFeatureGuard from '@/extension/context/content/components/blueticks-panel/ShadowFeatureGuard';
import { nextReportRetryDelayMs, reportEngineConnectedOnceThunk } from '../../thunks/report-engine-connected';
import FirstWeekCard, { FIRST_WEEK_ROW_HEIGHT_EXTENSION } from '@common/components/first-week-card/first-week-card';


function TopHeaderMain() {
  const { t, i18n } = useTranslation(namespace);
  // The injected top bar has to work out its own direction. `applyDocumentDirection`
  // deliberately never touches the WhatsApp host page (see services/rtl.ts), so the
  // `dir` on <html> is WhatsApp's, not ours — the language is the only signal we own.
  // Both rows used to be pinned to `direction: 'ltr'`, so the whole bar stayed
  // left-to-right for Hebrew/Arabic users while WhatsApp itself mirrored around it.
  const dir: 'ltr' | 'rtl' = i18n.dir(i18n.language) === 'rtl' ? 'rtl' : 'ltr';
  const isRtl = dir === 'rtl';
  const user = useAppSelector(selectUser);
  const userIsLoggedIn = !!user?._id;
  const dispatch = useAppDispatch()
  const whatsappReady = useAppSelector((state) => state.WhatsAppReducer.me?.id?._serialized ?? state.WhatsAppReducer.me?._id);
  const reportingUserId = user?._id;
  // Report once signed in. The thunk resolves the engine identity itself (the `me` event is
  // not guaranteed to fire) and returns false until it can, so a bounded backoff retry covers readiness.
  useEffect(() => {
    if (!reportingUserId) return undefined;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const report = async () => {
      const acknowledged = await dispatch(reportEngineConnectedOnceThunk()).unwrap();
      if (stopped || acknowledged) return;
      const delay = nextReportRetryDelayMs(attempt++);
      if (delay !== undefined) retry = setTimeout(report, delay);
    };
    void report();
    return () => { stopped = true; clearTimeout(retry); };
  }, [dispatch, reportingUserId, whatsappReady]);
  const isLabelsEnabled = useAppSelector(isLabelsEnabledSelector);
  // Hide the duplicate language switcher when the Blueticks side
  // panel is open — the panel ships its own LanguageSwitcher in the
  // same compact mode, so showing both would mean two identical flags
  // a few pixels apart (user feedback).
  const isBlueticksPanelOpen = useAppSelector(
    (s: any) => !!s.BlueticksPanelReducer?.open,
  );
  // When the user hides the labels row via Display Settings the
  // RowStack collapses with `display: none` — but the parent's
  // `padding-top` was sized for the 2-row header. Read the visibility
  // flag so the padding effect below can drop back to single-row
  // height and the page doesn't carry an empty 48px gap.
  const isLabelFilterRowVisible = useAppSelector(
    (s: any) => s.DisplaySettingsReducer?.labelFilterRow ?? true,
  );
  const showLabelsRow = isLabelsEnabled && isLabelFilterRowVisible;

  // Top-bar buttons + status indicators. When every single one is
  // hidden, the row has nothing to show — collapse it entirely so
  // there's no empty 48px strip eating the top of the page.
  const topBarFlags = useAppSelector((s: any) => {
    const ds = s.DisplaySettingsReducer ?? {};
    return [
      // Buttons section
      ds.topBtnInitError, ds.topBtnLogo, ds.topBtnOpenMessages,
      ds.topBtnLaunchCampaign, ds.topBtnOpenChat, ds.topBtnToggleLabels,
      ds.topBtnManageCampaigns, ds.topBtnReminders,
      ds.topBtnDebugDiag,
      // Status section
      ds.subscriptionBadge, ds.connectedEngines, ds.botStatus, ds.updateBadge,
    ];
  });
  // A flag that's `undefined` (no preference saved yet) counts as
  // "visible" — same default as everywhere else in this module.
  const showTopBarRow = topBarFlags.some((f: any) => f !== false);
  // const [isSideBarVersion, setIsSideBarVersion] = useState(false);

  // useEffect(() => {
  //   document.querySelector('[tabindex="-1"] > header') ? setIsSideBarVersion(true) : setIsSideBarVersion(false);
  // }, []);

  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  //const managerOrCampaigner = activeSubscription?.plan.planType === "Manager" || activeSubscription?.plan.planType === "Campaigner";

  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);

  // #113 FE-1 onboarding row renders below the blueticks bar (a slim 48px
  // strip). It self-gates (guided arm / not dismissed / not complete) and
  // reports visibility so we can grow the header height only while it shows.
  const [firstWeekVisible, setFirstWeekVisible] = useState(false);

  useEffect(() => {
    dispatch(getWaVersionThunk())
    if (userIsLoggedIn) {
      dispatch(loadLabelsAndContactLabelsThunk())
      waQueryContactsAndChatsTool({})
    }
  }, [userIsLoggedIn])

  // Dynamically update parent container padding to match the rows
  // we'll actually paint. 0px / 48px / 96px depending on the
  // combinations of {top bar visible, labels row visible}.
  useEffect(() => {
    const topBarHeight = showTopBarRow ? 48 : 0;
    const labelsHeight = showLabelsRow ? 48 : 0;
    const firstWeekHeight = firstWeekVisible ? FIRST_WEEK_ROW_HEIGHT_EXTENSION : 0;
    const total = topBarHeight + labelsHeight + firstWeekHeight;
    const headerHeight = `${total}px`;
    const topHeader = document.getElementById('top-header') as HTMLElement;
    // Resolve the container via our own host node, not the `.bt-header-parent`
    // class: WhatsApp's React rewrites `.two`'s className (e.g. toggling
    // `app-animating`) and drops our class, so the lookup returned null and the
    // padding froze at its first value while #top-header grew to 96px — the
    // labels row then covered the chat header until a page refresh.
    const parentContainer = (topHeader?.parentElement
      ?? document.querySelector('.bt-header-parent')) as HTMLElement | null;

    if (parentContainer) {
      parentContainer.style.paddingTop = headerHeight;

      // Also update any absolute/fixed positioned children
      Array.from(parentContainer.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) return;
        if (child.id === 'top-header' || child.tagName === 'STYLE') return;
        const computedStyle = window.getComputedStyle(child);
        if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') {
          child.style.top = headerHeight;
          child.style.height = `calc(100% - ${headerHeight})`;
        }
      });
    }

    if (topHeader) {
      topHeader.style.height = headerHeight;
    }
  }, [showLabelsRow, showTopBarRow, firstWeekVisible])

  // Check if version check feature is enabled
  const versionCheckEnabled = true // localStorage.getItem('btVersionCheck') === 'true';

  return (
    <BTThemeProvider >
      {/* Visibility-rule stylesheet for this shadow root. The Display
          Settings page toggles `<html>` classes; this guard maps them
          to `display: none` rules for our `data-bt-feature` wrappers
          inside the same shadow. */}
      <ShadowFeatureGuard />
      <div style={{ width: '100%' }} >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <RowStack sx={{
            direction: dir,
            position: 'relative',
            zIndex: 100,
            justifyContent: 'space-between',
            padding: '0px 16px',
            height: 48,
            borderBottom: '1px solid #dbdcdf',
            backgroundColor: 'var(--color-surface)',
            // Collapse the row entirely (no 48px gap) when every
            // child toggle is off.
            display: showTopBarRow ? 'flex' : 'none',
          }}>

            <RowStack id="header-left-side" flexGrow={"3"}>
              <div data-bt-feature="topButtonBar" style={{ display: 'contents' }}>
                <ExtensionTopButtonBar />
              </div>
            </RowStack>

            <RowStack id="header-right-side" sx={{ flex: 'auto' }} gap={"10px"} direction={"row-reverse"} >
              {/* <LanguageSwitcher /> */}

              {userIsLoggedIn &&
                <>
                  {!isBlueticksPanelOpen && <LanguageSwitcher hideLabel={true} />}
                  <div data-bt-feature="updateBadge">
                    <UpdateNotificationBadge />
                  </div>
                  <div data-bt-feature="botStatus">
                    <BotStatusComponent triggerMode="quickAgentsSidebar" />
                  </div>
                  <div data-bt-feature="connectedEngines">
                    <ConnectedEngines />
                  </div>
                  <div data-bt-feature="subscriptionBadge">
                    <SubscriptionStatusBar compact />
                  </div>
                </>
              }

              {!userIsLoggedIn &&
                <BTButton sx={isRtl ? { marginLeft: '15px' } : { marginRight: '15px' }} onClick={() => dispatch(accessFormPopupActions.setStatus('Login'))}>
                  {t('loginForm.loginButton')}
                </BTButton>
              }
              {userIsLoggedIn &&
                <>
                  <ConditionalWrapper shouldLoadSelector={(state) => isAnyCampaignInProgressSelector(state)}>
                    <InProgressCampaignsIndicatorDialog />
                  </ConditionalWrapper>
                </>
              }
            </RowStack>

          </RowStack >

          {userIsLoggedIn && isLabelsEnabled && (
            <RowStack
              data-bt-feature="labelFilterRow"
              sx={{
              direction: dir,
              position: 'relative',
              zIndex: 99,
              justifyContent: 'space-between',
              padding: '0px 16px',
              height: 48,
              borderBottom: '1px solid #dbdcdf',
              backgroundColor: 'var(--color-surface)',
              width: '100%'
            }}>
              <div
                className="flex items-center gap-1"
                // Set explicitly per direction rather than with a logical property:
                // this build's CSS pipeline flattens margin-inline-* to physical
                // margins, so `marginInlineEnd` would not mirror.
                style={isRtl ? { marginLeft: '15px' } : { marginRight: '15px' }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        if (userIsLoggedIn) {
                          dispatch(labelActions.toggleNewLabel(true))
                        } else {
                          dispatch(accessFormPopupActions.setStatus('Login'))
                        }
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <Plus size={20} className="text-gray-600" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('labels.newLabel')}</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <LabelTopHeader />
              <div className="flex items-center gap-1">


                <LabelSettingsPopper />
              </div>
              <LabelEmptyState dir={dir} />
            </RowStack>
          )}

          {/* #113 FE-1: slim onboarding progress row, below the blueticks bar. */}
          <FirstWeekCard
            surface="extension"
            dir={dir}
            onVisibilityChange={setFirstWeekVisible}
          />
        </div>
      </div>
    </BTThemeProvider >
  )
}



export default memo(TopHeaderMain);
